import React, { useState } from "react";
import axios from "axios";
import { FileText, Download, Calendar, FileDown, Users, MapPin, Leaf, GraduationCap, Shield } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";

const baseUrl =
  process.env.REACT_APP_PUBLIC_API_URL  

const REPORT_TYPES = [
  {
    id: "demographics",
    label: "Demographics Report",
    description: "Age, gender, location, and education statistics",
    endpoint: "/demographics",
    dataKey: null,
  },
  {
    id: "regional",
    label: "Regional Analysis",
    description: "Breakdown by region, district, and community",
    endpoint: "/regional-distribution",
    dataKey: "distribution",
  },
  {
    id: "agriculture",
    label: "Agriculture Interest Report",
    description: "Value chains, engagement levels, & resources",
    endpoint: "/value-chain",
    dataKey: null,
  },
  {
    id: "education",
    label: "Education & Training",
    description: "Education levels, enrollment status, and occupation",
    endpoint: "/education-level",
    dataKey: "education",
  },
  {
    id: "safeguarding",
    label: "Safeguarding Analysis Report",
    description: "Safety perception, risks, and reporting channels",
    endpoint: "/safety-perception",
    dataKey: "safetyPerception",
  },
];

const DATE_RANGES = [
  { value: "all", label: "All Time" },
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

const EXPORT_FORMATS = [
  { value: "csv", label: "CSV" },
  { value: "pdf", label: "PDF" },
];

const escapeCsv = (val) => {
  const s = val == null ? "" : String(val).trim();
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const objectToRows = (obj, reportLabel, skipKeys = ["success", "message"]) => {
  const rows = [];
  if (!obj || typeof obj !== "object") return rows;
  const entries = Object.entries(obj).filter(([k]) => !skipKeys.includes(k));
  for (const [key, value] of entries) {
    if (Array.isArray(value)) {
      value.forEach((item) =>
        rows.push({
          report: reportLabel,
          category: key,
          ...(typeof item === "object" && item !== null ? item : { value: item }),
        })
      );
    } else if (value !== null && typeof value === "object" && !(value instanceof Date)) {
      rows.push({ report: reportLabel, metric: key, ...value });
    } else {
      rows.push({ report: reportLabel, metric: key, value });
    }
  }
  return rows;
};

const barChartHtml = (items, labelKey = "label", valueKey = "count", maxVal = null) => {
  const list = Array.isArray(items) ? items : [];
  const max = maxVal ?? Math.max(...list.map((i) => Number(i[valueKey] ?? i.value ?? 0)), 1);
  return list
    .map(
      (i) => {
        const label = i[labelKey] ?? i.name ?? i.region ?? i.district ?? "";
        const val = Number(i[valueKey] ?? i.value ?? 0);
        const pct = max > 0 ? Math.round((val / max) * 100) : 0;
        return `<div style="margin-bottom:8px;"><div style="display:flex;justify-content:space-between;font-size:12px;margin-bottom:2px;"><span>${escapeHtml(String(label))}</span><span>${val}</span></div><div style="height:16px;background:#e5e7eb;border-radius:4px;overflow:hidden;"><div style="height:100%;width:${pct}%;background:#15803d;border-radius:4px;"></div></div></div>`;
      }
    )
    .join("");
};

const escapeHtml = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const Reports = () => {
  const [selectedReports, setSelectedReports] = useState({});
  const [dateRange, setDateRange] = useState("all");
  const [exportFormat, setExportFormat] = useState("csv");
  const [generating, setGenerating] = useState(false);
  const [quickExporting, setQuickExporting] = useState(false);

  const toggleReport = (id) => {
    setSelectedReports((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const FULL_CSV_COLUMNS = [
    "registrationId", "firstName", "lastName", "middleName", "dateOfBirth", "gender", "ageGroup", "idNumber", "disability",
    "region", "district", "community", "phoneNumber", "email", "whatsapp",
    "highestEducation", "enrollmentStatus", "completionStatus", "mainOccupation",
    "interestInAgriculture", "valueChainInterest", "currentEngagement",
    "landAccess", "financeAccess", "toolsTechnologyAccess", "mobilephoneAccess",
    "safetyPerception", "riskHarmful", "reportIssues", "safeguardinginfo",
    "created_at", "age", "date",
  ];

  const fetchAllDataCsv = async () => {
    setQuickExporting(true);
    try {
      const res = await axios.get(`${baseUrl}/list`, {
        params: { limit: 10000, full: true },
      });
      const data = res.data;
      if (!data.success || !Array.isArray(data.registrations)) {
        throw new Error(data.message || "Failed to fetch data");
      }
      const list = data.registrations;
      const columns = list.length > 0
        ? FULL_CSV_COLUMNS.filter((c) => list[0][c] !== undefined).length
          ? FULL_CSV_COLUMNS.filter((c) => list[0][c] !== undefined)
          : Object.keys(list[0])
        : FULL_CSV_COLUMNS;
      const headerRow = columns.map(escapeCsv).join(",");
      const dataRows = list.map((row) =>
        columns.map((col) => escapeCsv(row[col])).join(",")
      );
      const csv = [headerRow, ...dataRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `all-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to export. Please try again.");
    } finally {
      setQuickExporting(false);
    }
  };

  const buildPdfReportHtml = async (selected) => {
    const sections = [];
    const dateLabel = DATE_RANGES.find((d) => d.value === dateRange)?.label || dateRange;


    // Helper to safely get .data or return empty object
    const safeData = (res) => (res && typeof res === 'object' && 'data' in res ? res.data : {});

    if (selected.includes("demographics")) {
      let demoRes, regionalRes, eduRes;
      try {
        [demoRes, regionalRes, eduRes] = await Promise.all([
          axios.get(`${baseUrl}/demographics`).catch(() => undefined),
          axios.get(`${baseUrl}/regional-distribution`).catch(() => undefined),
          axios.get(`${baseUrl}/education-level`).catch(() => undefined),
        ]);
      } catch {}
      const d = safeData(demoRes);
      const dist = safeData(regionalRes)?.distribution || [];
      const edu = safeData(eduRes)?.education || [];
      let html = "<h2>Demographics Report</h2>";
      html += "<p><strong>Age range:</strong> Minimum " + (d.minAge ?? "—") + " years, Maximum " + (d.maxAge ?? "—") + " years</p>";
      html += "<p><strong>Total registrants:</strong> " + (d.totalRegistrations ?? 0) + "</p>";
      html += "<h3>Age groups</h3>" + barChartHtml(d.ageGroups || [], "label", "count");
      html += "<h3>Gender</h3>" + barChartHtml([{ label: "Male", count: d.male || 0 }, { label: "Female", count: d.female || 0 }], "label", "count");
      html += "<h3>Location (by region)</h3>" + barChartHtml(dist, "region", "count");
      html += "<h3>Education statistics</h3>" + barChartHtml(edu, "label", "count");
      sections.push(html);
    }

    if (selected.includes("regional")) {
      let distRes, topRegRes, topDistRes;
      try {
        [distRes, topRegRes, topDistRes] = await Promise.all([
          axios.get(`${baseUrl}/regional-distribution`).catch(() => undefined),
          axios.get(`${baseUrl}/top-regions`).catch(() => undefined),
          // axios.get(`${baseUrl}/top-districts`).catch(() => undefined),
        ]);
      } catch {}
      const dist = safeData(distRes)?.distribution || [];
      const topRegions = safeData(topRegRes)?.top5Regions || [];
      const topDistricts = safeData(topDistRes)?.districts || [];
      const topRegion = topRegions[0];
      const topDistrict = topDistricts[0];
      let html = "<h2>Regional Analysis</h2>";
      if (topRegion) html += "<p><strong>Region with most registrants:</strong> " + escapeHtml(topRegion.name) + " (" + topRegion.count + ")</p>";
      if (topDistrict) html += "<p><strong>District with most registrants:</strong> " + escapeHtml(topDistrict.district) + " (" + topDistrict.count + ")</p>";
      html += "<h3>Breakdown by region</h3>" + barChartHtml(dist, "region", "count");
      // html += "<h3>Top 10 districts</h3>" + barChartHtml(topDistricts.map((x) => ({ label: x.district, count: x.count })), "label", "count");
      sections.push(html);
    }

    if (selected.includes("agriculture")) {
      let vcRes, engRes, resRes;
      try {
        [vcRes, engRes, resRes] = await Promise.all([
          axios.get(`${baseUrl}/value-chain-distribution`).catch(() => undefined),
          axios.get(`${baseUrl}/current-engagement`).catch(() => undefined),
          axios.get(`${baseUrl}/resources`).catch(() => undefined),
        ]);
      } catch {}
      const vc = safeData(vcRes)?.distribution || [];
      const eng = safeData(engRes)?.engagement || [];
      const res = safeData(resRes) || {};
      let html = "<h2>Agriculture Interest Report</h2>";
      html += "<h3>Value chain interest</h3>" + barChartHtml(vc, "label", "value");
      html += "<h3>Engagement levels</h3>" + barChartHtml(eng, "name", "count");
      html += "<h3>Resources</h3>";
      if (res.landAccess) html += "<h4>Land access</h4>" + barChartHtml(res.landAccess, "label", "count");
      if (res.financeAccess) html += "<h4>Finance access</h4>" + barChartHtml(res.financeAccess, "label", "count");
      if (res.toolsTechnology) html += "<h4>Tools/technology</h4>" + barChartHtml(res.toolsTechnology, "label", "count");
      if (res.mobilephone) html += "<h4>Mobile phone</h4>" + barChartHtml(res.mobilephone, "label", "count");
      sections.push(html);
    }

    if (selected.includes("education")) {
      let res;
      try {
        res = await axios.get(`${baseUrl}/education-level`).catch(() => undefined);
      } catch {}
      const edu = safeData(res)?.education || [];
      sections.push("<h2>Education & Training</h2><h3>Education levels</h3>" + barChartHtml(edu, "label", "count"));
    }

    if (selected.includes("safeguarding")) {
      let res;
      try {
        res = await axios.get(`${baseUrl}/safety-perception`).catch(() => undefined);
      } catch {}
      const safety = safeData(res)?.safetyPerception || [];
      sections.push("<h2>Safeguarding Analysis Report</h2><h3>Safety perception</h3>" + barChartHtml(safety, "label", "count"));
    }

    return `
      <!DOCTYPE html>
      <html><head><meta charset="utf-8"/><title>Report</title><style>body{font-family:sans-serif;padding:24px;max-width:800px;margin:0 auto;} h2{margin-top:28px;border-bottom:1px solid #e5e7eb;padding-bottom:8px;} h3{margin-top:16px;}</style></head>
      <body>
        <h1>EYES Ghana – Generated Report</h1>
        <p><strong>Date range:</strong> ${dateLabel}</p>
        <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
        <hr/>
        ${sections.join("<hr/>")}
      </body></html>`;
  };

  const generateReport = async () => {
    const selected = Object.entries(selectedReports).filter(([, v]) => v).map(([id]) => id);
    if (selected.length === 0) {
      alert("Please select at least one report.");
      return;
    }
    setGenerating(true);
    try {
      if (exportFormat === "pdf") {
        const html = await buildPdfReportHtml(selected);
        const printWindow = window.open("", "_blank");
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
      } else {
        const allRows = [];
        for (const id of selected) {
          const config = REPORT_TYPES.find((r) => r.id === id);
          if (!config) continue;
          const res = await axios.get(`${baseUrl}${config.endpoint}`);
          const data = res.data;
          let payload = config.dataKey ? data?.[config.dataKey] : data;
          const rows = objectToRows(payload, config.label);
          allRows.push(...rows);
        }
        if (allRows.length === 0) {
          const headerRow = "report,message";
          const csv = headerRow + "\n" + escapeCsv("No data for selected reports");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `report-${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        } else {
          const headers = Object.keys(allRows[0]);
          const headerRow = headers.map(escapeCsv).join(",");
          const dataRows = allRows.map((row) =>
            headers.map((h) => escapeCsv(row[h])).join(",")
          );
          const csv = [headerRow, ...dataRows].join("\n");
          const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `report-${new Date().toISOString().slice(0, 10)}.csv`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Failed to generate report. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const reportIcons = {
    demographics: <Users size={20} className="text-green-600" />,
    regional: <MapPin size={20} className="text-green-600" />,
    agriculture: <Leaf size={20} className="text-green-600" />,
    education: <GraduationCap size={20} className="text-green-600" />,
    safeguarding: <Shield size={20} className="text-green-600" />,
  };

  return (
    <AppLayout>
      <div className="p-6 bg-gray-50 min-h-full">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Reports & Exports</h1>
          <p className="text-gray-500 text-sm mt-1">
            Generate comprehensive reports and export data in multiple formats
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Select Reports */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Select Reports</h2>
              <p className="text-sm text-gray-500 mt-0.5">Choose one or more reports to generate.</p>
            </div>
            <div className="p-6 space-y-4 max-h-[400px] overflow-y-auto">
              {REPORT_TYPES.map((report) => (
                <div
                  key={report.id}
                  className="flex items-start gap-4 p-4 rounded-lg border border-gray-100 hover:border-green-200 transition-colors"
                >
                  <input
                    type="checkbox"
                    id={report.id}
                    checked={!!selectedReports[report.id]}
                    onChange={() => toggleReport(report.id)}
                    className="mt-1 h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <div className="flex-1 min-w-0">
                    <label htmlFor={report.id} className="flex items-center gap-2 cursor-pointer">
                      {reportIcons[report.id]}
                      <span className="font-medium text-gray-900">{report.label}</span>
                    </label>
                    <p className="text-sm text-gray-500 mt-1">{report.description}</p>
                    <div className="flex gap-2 mt-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        PDF
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        CSV
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Report Options + Quick Export */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">Report Options</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date Range</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={dateRange}
                      onChange={(e) => setDateRange(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {DATE_RANGES.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Export Format</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <select
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      {EXPORT_FORMATS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={generateReport}
                  disabled={generating}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-700 text-white rounded-lg text-sm font-semibold hover:bg-green-800 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  <Download size={18} />
                  {generating ? "Generating…" : "Generate Report"}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">Quick Export</h2>
              </div>
              <div className="p-6 space-y-3">
                <p className="text-sm text-gray-500">Download all registration data as CSV with no filters.</p>
                <button
                  type="button"
                  onClick={fetchAllDataCsv}
                  disabled={quickExporting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  <FileDown size={18} />
                  {quickExporting ? "Exporting…" : "All Data (CSV)"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Reports;
